import React, { useEffect, useState } from 'react';
import { getMemeLayerStatus } from '../../services/StrategiesService';

/**
 * Guia de rentabilidade — conhecimento destilado da operação real (auditorias 06-07/2026).
 * Estático e educativo: NÃO altera nada; ensina o que ligar em cada perfil e por quê.
 * Versão completa (com evidências e rastreabilidade): PLAYBOOK-RENTABILIDADE.md na raiz do repo.
 * Atualizar os dois juntos.
 */

// Setup recomendado por perfil. Colunas na mesma ordem do seletor de perfil (🛡️ ⚖️ 🚀).
const PROFILE_ROWS = [
    { label: 'Confiança mínima da IA', c: '0,75', m: '0,65', a: '0,55', why: '0,75 perde oportunidades demais; a saída usa sempre 0,05 a menos (sair é mais fácil que entrar)' },
    { label: 'Stop de proteção', c: '2%', m: '2%', a: '5%', why: 'com TP a partir de 1%, stop 3% exige 75% de acerto pra empatar; 2% derruba o empate pra ~67% (recalibrado 07/2026)' },
    { label: '% da carteira por compra', c: '15', m: '25', a: '40', why: 'grande demais concentra; pequena demais paga taxa à toa' },
    { label: 'Lucro mínimo p/ vender por IA', c: '1%', m: '1%', a: '1,5%', why: 'vender na primeira migalha entrega o ganho pra taxa; a escada de take-profit trabalha melhor' },
    { label: 'Tamanho por volatilidade (ATR)', c: 'Sim (0,5%/trade)', m: 'Sim (1%/trade)', a: 'Sim (2%/trade)', why: 'mesmo risco em moeda calma e nervosa — ligar na demo primeiro' },
    { label: 'Braço de repique', c: 'Não', m: 'Opcional', a: 'Sim', why: 'trade rápido de alta vol.; só com as travas dele (stop 2%, saída por tempo 12h, RSI 42)' },
    { label: 'Consenso 2-de-3 IAs', c: 'Sim', m: 'Opcional', a: 'Não', why: 'menos sinais falsos, ao custo de menos trades' },
    { label: 'Alocação entre estratégias', c: 'Igual', m: 'Igual → Desempenho', a: 'Desempenho (piso 10/teto 50)', why: 'capital segue quem entrega — mas só com 30+ trades de amostra' },
    { label: 'Earn (caixa rendendo)', c: 'Sim', m: 'Sim', a: 'Sim', why: 'reserva parada deve render; nunca toca o capital das estratégias' },
    { label: 'Drawdown diário / semanal', c: '5% / 12%', m: '5% / 12%', a: '5% / 12%', why: 'INEGOCIÁVEL em todos os perfis — agressividade é tamanho e seleção, nunca tirar o freio' },
];

const RULES = [
    'O braço vendedor por sinal é quem paga a conta (71% do lucro com 4% dos trades) — cuidado com proteções que o bloqueiam.',
    'Taxa é sócio silencioso: ~25% do lucro bruto em conta pequena. Saídas maker sempre ligadas.',
    'Dimensione pela volatilidade (ATR), não por % fixo — mesma perda potencial em qualquer moeda.',
    'Estatística com menos de ~30 trades mente (win-rate de 20 trades erra ±10 pontos). Não realoque capital sem amostra.',
    'Diversifique de verdade: 1 símbolo pagando 97% do lucro é sorte concentrada, não habilidade.',
    'Corte o que sangra repetido — prejuízo líquido com 10+ trades e acerto <50% = pausar o símbolo (o Coach mostra). O Disjuntor de perdas por ativo faz isso automático: 3 vendas seguidas no prejuízo → pausa a compra 6h.',
    'Caixa parado é prejuízo invisível: 38% da conta líquida sem render foi o medido — reserva vai pro Earn.',
    'Repique tem prazo de validade: não repicou em 12h, sai (a tese é rápida ou está errada).',
    'Comportamento novo: liga na demo, observa 1+ dia, um opt-in por vez, só então prod.',
    'Orquestrador de regime só age se estiver ROTULADO: ligar o mestre + marcar a estratégia não bastam. Rotule pelo botão "Rotular pela simulação" ou ligue "Rotular regime automaticamente".',
    'IA: free/barato no volume (sinal de trade), pago só nas decisões raras (reavaliação semanal, revisão de risco) — configure nos Papéis da IA da tela de provedores; a tela alerta provedor pago no sinal e provedor mudo há 24h.',
];

// Card da camada MEME (P5 item 3, PLANO-CAMADA-MEME item 3): estado vivo — orçamento configurado,
// data do aceite e o indicador do disjuntor por camada (utils/layerBreaker, filtrado pra MEME).
// Read-only aqui (não liga/desliga nada) — ligar a camada é feito pelo modal de aceite ao ativar
// uma estratégia [Meme Momentum] (ver MemeLayerAcceptModal). Fail-open: erro no fetch some com o
// card em vez de quebrar o guia.
function MemeLayerCard() {
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        getMemeLayerStatus()
            .then(s => setStatus(s))
            .catch(() => setError(true));
    }, []);

    if (error || !status) return null;

    const { budgetUsdt, acceptedAt, breaker } = status;
    const fmtDate = d => d ? new Date(d).toLocaleString('pt-BR') : null;

    let breakerText;
    if (!breaker || !breaker.enabled) {
        breakerText = 'disjuntor OFF (recomendado ON)';
    } else if (breaker.tripped) {
        breakerText = 'DESARMADO hoje — rearma amanhã';
    } else {
        const pnl = breaker.accPnl != null ? breaker.accPnl.toFixed(2) : '0,00';
        const cap = breaker.capValue != null ? breaker.capValue.toFixed(2) : '?';
        breakerText = `armado (P&L do dia ${pnl} de ${cap} USDT)`;
    }

    return (
        <div className="card card-body border mt-3">
            <label className="form-label fw-bold mb-1">🐸 Camada MEME</label>
            <div className="text-muted small">
                Orçamento: <strong>{budgetUsdt != null ? `${budgetUsdt} USDT` : 'não configurado'}</strong>
                {' · '}Aceite do risco: <strong>{acceptedAt ? fmtDate(acceptedAt) : 'nunca aceito'}</strong>
                {' · '}Disjuntor: <strong>{breakerText}</strong>
            </div>
        </div>
    );
}

function ProfitabilityGuide() {
    const [open, setOpen] = useState(false);

    return (
        <div className="mb-3">
            <hr className="my-3" />
            <div className="d-flex align-items-center justify-content-between">
                <div>
                    <label className="form-label fw-bold mb-0">📈 Guia de rentabilidade</label>
                    <div className="text-muted small">O que os dados da operação real ensinaram — recomendações por perfil e regras práticas.</div>
                </div>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setOpen(v => !v)}>
                    {open ? 'Esconder' : 'Ver guia'}
                </button>
            </div>

            {open && (
                <div className="mt-3">
                    <div className="table-responsive">
                        <table className="table table-sm align-middle">
                            <thead>
                                <tr>
                                    <th>Ajuste</th>
                                    <th>🛡️ Conservador</th>
                                    <th>⚖️ Moderado</th>
                                    <th>🚀 Agressivo</th>
                                    <th className="d-none d-lg-table-cell">Por quê</th>
                                </tr>
                            </thead>
                            <tbody>
                                {PROFILE_ROWS.map(r => (
                                    <tr key={r.label}>
                                        <td className="fw-semibold">{r.label}</td>
                                        <td>{r.c}</td>
                                        <td>{r.m}</td>
                                        <td>{r.a}</td>
                                        <td className="d-none d-lg-table-cell text-muted small">{r.why}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <label className="form-label fw-bold mt-2 mb-1">Regras que os dados provaram</label>
                    <ul className="text-muted small mb-2">
                        {RULES.map((t, i) => <li key={i}>{t}</li>)}
                    </ul>
                    <div className="text-muted small fst-italic">
                        Fonte: auditorias com dados reais (06-07/2026). Versão completa com evidências: PLAYBOOK-RENTABILIDADE.md no repositório.
                    </div>

                    <MemeLayerCard />
                </div>
            )}
        </div>
    );
}

export default ProfitabilityGuide;
